import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

const BulkPreviewModal = ({ isOpen, onClose, schedulingResult, onConfirm }) => {
  const [treatmentInstructions, setTreatmentInstructions] = useState({});
  const [generalInstruction, setGeneralInstruction] = useState('');

  // Reset instructions when modal opens with new scheduling result
  useEffect(() => {
    if (schedulingResult?.proposedSchedule) {
      setTreatmentInstructions(
        schedulingResult.proposedSchedule.reduce((acc, slot) => ({
          ...acc,
          [slot.treatment]: ''
        }), {})
      );
      setGeneralInstruction(schedulingResult.generalInstruction || '');
    }
  }, [schedulingResult]);

  if (!isOpen || !schedulingResult || !schedulingResult.proposedSchedule) return null;

  const firstSlot = schedulingResult.proposedSchedule[0];

  const handleInstructionChange = (treatment, value) => {
    setTreatmentInstructions(prev => ({
      ...prev,
      [treatment]: value
    }));
  };

  const handleGeneralInstructionChange = (e) => {
    setGeneralInstruction(e.target.value);
  };

  const handleConfirm = () => {
    onConfirm({
      treatmentInstructions,
      generalInstruction,
      slots: schedulingResult.proposedSchedule
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold">Package Schedule Preview</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Guest Info and General Instructions Header */}
          <div className="bg-gray-50 p-4 rounded-md mb-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><strong>Guest:</strong> {firstSlot.guestName}</div>
              <div><strong>Guest ID:</strong> {firstSlot.guestId}</div>
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                General Instructions
              </label>
              <textarea
                value={generalInstruction}
                onChange={handleGeneralInstructionChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows="2"
                placeholder="Enter general instructions for all treatments"
              />
            </div>
          </div>

          {/* Slots Table */}
          <div className="space-y-4">
            {schedulingResult.proposedSchedule.map((slot, index) => (
              <div key={index} className="border rounded-md p-4">
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <strong>Date:</strong><br />
                    {slot.date}
                  </div>
                  <div>
                    <strong>Treatment:</strong><br />
                    {slot.treatment}
                  </div>
                  <div>
                    <strong>Time:</strong><br />
                    {slot.startTime} - {slot.endTime}
                  </div>
                  <div>
                    <strong>Therapist:</strong><br />
                    {slot.therapist}
                  </div>
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Treatment Instructions
                  </label>
                  <textarea
                    value={treatmentInstructions[slot.treatment]}
                    onChange={(e) => handleInstructionChange(slot.treatment, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="2"
                    placeholder={`Enter specific instructions for ${slot.treatment}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {schedulingResult.failedSlots && schedulingResult.failedSlots.length > 0 && (
            <div className="mt-4 bg-red-50 p-4 rounded-md">
              <h4 className="font-semibold text-red-700">Unavailable Slots:</h4>
              <ul className="mt-2 list-disc pl-5 text-red-600">
                {schedulingResult.failedSlots.map((slot, index) => (
                  <li key={index}>
                    {slot.treatment} on {slot.date}: {slot.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              Confirm Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPreviewModal;
